import React, { FormEvent, useState } from 'react';
import axios from '../axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastProvider';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo_with_text_lg.png';

// Critical LCP asset loaded from public folder for optimal performance
const clouds = '/assets/images/clouds.webp';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');

  // Calculate daily changing hue value for cloud background
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 0);
  const diff = today.getTime() - startOfYear.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  const hue = dayOfYear % 360;

  const backgroundStyle: React.CSSProperties = {
    backgroundColor: 'white',
    position: 'relative',
    overflow: 'hidden',
  };

  const cloudImageStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: `url(${clouds})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    filter: `hue-rotate(${hue}deg) saturate(70%) brightness(100%)`,
    opacity: 1.0,
    zIndex: 0,
  };

  const handleRequestReset = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await axios.post('/api/auth/forgot-password', null, { params: { email } });
      toast.show(t('auth.resetRequestSent') as string, 'success');
    } catch (error: any) {
      toast.show(String(error.response?.data || t('auth.resetFailed')), 'error');
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch') as string);
      setLoading(false);
      return;
    }

    if (!token) {
      setError(t('auth.invalidResetToken') as string);
      setLoading(false);
      return;
    }

    if (password.length < 6 || password.length > 40) {
      setError(t('auth.passwordLengthError') as string);
      setLoading(false);
      return;
    }

    try {
      await axios.post('/api/auth/reset-password', {
        token,
        newPassword: password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || (t('auth.resetPasswordFailed') as string));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={backgroundStyle} className="min-h-screen flex items-center justify-center px-4">
      <div style={cloudImageStyle}></div>
      {/* Global overlay above clouds for auth pages */}
      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-[1]"></div>

      <div className="relative z-10 w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <img src={logo} alt="HeartPort logo" className="h-14 md:h-20 w-auto" />
        </div>

        <div>
          <h2 className="text-center text-4xl font-bold text-[#49ABDB]">
            {token ? t('auth.resetYourPassword') : t('auth.reset')}
          </h2>
        </div>

        <div className="bg-[#CAD4DD] p-8 rounded-lg shadow-lg">
          {token ? (
            success ? (
              <div className="text-center">
                <p className="text-green-600">{t('auth.resetSuccess')}</p>
                <p className="text-sm text-gray-600 mt-2">{t('auth.redirectingToLogin')}</p>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={handleResetPassword}>
                <div>
                  <label className="block text-sm font-medium text-[#111D4E] mb-1">{t('auth.newPassword')}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-gray-800"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111D4E] mb-1">{t('auth.confirmNewPassword')}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-gray-800"
                    required
                  />
                </div>
                {error && <div className="text-sm text-red-600">{error}</div>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#47ABDB] text-white py-2 rounded-md font-semibold hover:bg-[#3d8fb8] disabled:opacity-60"
                >
                  {loading ? t('auth.resettingPassword') : t('auth.resetPassword')}
                </button>
              </form>
            )
          ) : (
            <form className="space-y-4" onSubmit={handleRequestReset}>
              <div>
                <label className="block text-sm font-medium text-[#111D4E] mb-1">{t('auth.email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-gray-800"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#47ABDB] text-white py-2 rounded-md font-semibold hover:bg-[#3d8fb8]"
              >
                {t('auth.reset')}
              </button>
            </form>
          )}
          <div className="text-sm mt-4 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-[#47ABDB] hover:underline"
            >
              {t('auth.login')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
